import React, { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";

const COLORS = [
  "#ff4f9a",
  "#ff87c8",
  "#ffb3dd",
  "#ffd4eb",
  "#ffeaf5",
  "#ff6b9d",
];

// ✅ Mesma “fonte de verdade” do Dashboard:
// - field: users.plan
// - normaliza e mapeia qualquer variação antiga (starter/mensal/anual/vip)
// - gera apenas 4 buckets finais: Free / Plus / Premium / Diamond
// - usa contagem (não % arredondado) e deixa o Recharts calcular o ângulo corretamente
function PlansPieChart() {
  const [counts, setCounts] = useState({
    free: 0,
    plus: 0,
    premium: 0,
    diamond: 0,
  });
  const [loading, setLoading] = useState(true);

  // -----------------------------------------
  // Helpers
  // -----------------------------------------
  const normalizePlanKey = (raw) => {
    const p = String(raw || "").toLowerCase().trim();

    // ✅ Free
    if (!p || p === "free" || p === "gratis" || p === "gratuito") return "free";

    // ✅ Plus (antigos: starter / plus)
    if (p.includes("starter") || p.includes("plus")) return "plus";

    // ✅ Premium (antigos: mensal/monthly/premium)
    if (
      p.includes("mensal") ||
      p.includes("monthly") ||
      p.includes("premium")
    ) {
      return "premium";
    }

    // ✅ Diamond (antigos: anual/annual/diamond/vip)
    if (
      p.includes("anual") ||
      p.includes("annual") ||
      p.includes("diamond") ||
      p.includes("vip")
    ) {
      return "diamond";
    }

    // ✅ Fallback seguro (se vier algo inesperado, não “some” do gráfico)
    // Você pode trocar para "premium" se fizer mais sentido no seu negócio.
    return "free";
  };

  const formatPct = (value) => `${Number(value).toFixed(0)}%`;

  useEffect(() => {
    async function loadPlans() {
      try {
        const snap = await getDocs(collection(db, "users"));

        let free = 0;
        let plus = 0;
        let premium = 0;
        let diamond = 0;

        snap.forEach((d) => {
          const planKey = normalizePlanKey(d.data()?.plan);

          if (planKey === "free") free++;
          else if (planKey === "plus") plus++;
          else if (planKey === "premium") premium++;
          else if (planKey === "diamond") diamond++;
          else free++;
        });

        setCounts({ free, plus, premium, diamond });
      } catch (error) {
        console.error("Erro ao carregar planos (PlansPieChart):", error);
        setCounts({ free: 0, plus: 0, premium: 0, diamond: 0 });
      } finally {
        setLoading(false);
      }
    }

    loadPlans();
  }, []);

  const chartData = useMemo(() => {
    const total = counts.free + counts.plus + counts.premium + counts.diamond;

    if (!total) return [];

    // ✅ Mantém ordem consistente e remove categorias zeradas
    const base = [
      { name: "Free", key: "free", count: counts.free },
      { name: "Plus", key: "plus", count: counts.plus },
      { name: "Premium", key: "premium", count: counts.premium },
      { name: "Diamond", key: "diamond", count: counts.diamond },
    ].filter((x) => x.count > 0);

    // ✅ Recharts usa "value" para proporção
    return base.map((x) => ({
      name: x.name,
      value: x.count,
      count: x.count,
      percent: Math.round((x.count / total) * 100),
      total,
    }));
  }, [counts]);

  if (loading) {
    return (
      <div className="chartBox">
        <h3>Tipos de plano 💳</h3>
        <div className="chart-loading">Carregando gráfico...</div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="chartBox">
        <h3>Tipos de plano 💳</h3>
        <div className="chart-loading">Sem dados para exibir.</div>
      </div>
    );
  }

  return (
    <div className="chartBox">
      <h3>Tipos de plano 💳</h3>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              outerRadius="80%"
              dataKey="value"
              // ✅ label agora mostra % calculado corretamente com base na contagem real
              label={(entry) => `${entry.name}: ${formatPct(entry.percent)}`}
            >
              {chartData.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>

            <Tooltip
              // ✅ Tooltip mais útil: percent + quantidade
              formatter={(value, _name, props) => {
                const { payload } = props || {};
                const pct = payload?.percent ?? 0;
                const count = payload?.count ?? value ?? 0;
                return [`${pct}% (${count} usuários)`, "Plano"];
              }}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ff4f9a",
                borderRadius: "8px",
                fontSize: "12px",
              }}
            />

            <Legend
              // ✅ Mostra nome do plano e não polui
              wrapperStyle={{
                fontSize: "12px",
                paddingTop: "10px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default PlansPieChart;
